import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, FileText, Loader2 } from 'lucide-react';
import { APP_NAME } from '../config/branding';
import {
  PublicFormView as PublicFormViewType,
  FormFieldDefinition,
  getPublicForm,
  submitPublicForm,
} from '../utils/formsApi';

const PublicFormView: React.FC = () => {
  const { submissionId } = useParams<{ submissionId: string }>();
  const [formData, setFormData] = useState<PublicFormViewType | null>(null);
  const [responses, setResponses] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (submissionId) {
      fetchForm();
    }
  }, [submissionId]);

  const fetchForm = async () => {
    try {
      const data = await getPublicForm(submissionId!);
      setFormData(data);
      setResponses(data.responses || {});
      if (data.is_submitted) {
        setSubmitted(true);
      }
    } catch (err) {
      setError('Form not found or has expired');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    formData?.fields.forEach((field) => {
      if (field.required) {
        const value = responses[field.id];
        if (value === undefined || value === null || value === '') {
          errors[field.id] = 'This field is required';
        }
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await submitPublicForm(submissionId!, responses);
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  const updateResponse = (fieldId: string, value: any) => {
    setResponses((prev) => ({ ...prev, [fieldId]: value }));
    // Clear validation error when user starts typing
    if (validationErrors[fieldId]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      });
    }
  };

  const renderField = (field: FormFieldDefinition) => {
    const value = responses[field.id] ?? '';
    const hasError = !!validationErrors[field.id];

    const baseInputClass = `w-full px-4 py-3 border ${
      hasError ? 'border-red-500' : 'border-gray-300'
    } focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors`;

    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => updateResponse(field.id, e.target.value)}
            placeholder={field.placeholder}
            className={baseInputClass}
            disabled={submitted}
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => updateResponse(field.id, e.target.value)}
            placeholder={field.placeholder}
            rows={4}
            className={baseInputClass}
            disabled={submitted}
          />
        );

      case 'checkbox':
        return (
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => updateResponse(field.id, e.target.checked)}
              className="w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500"
              disabled={submitted}
            />
            <span className="text-gray-700">{field.label}</span>
          </label>
        );

      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => updateResponse(field.id, e.target.value)}
            className={baseInputClass}
            disabled={submitted}
          >
            <option value="">Select an option...</option>
            {field.options?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => updateResponse(field.id, e.target.value)}
            className={baseInputClass}
            disabled={submitted}
          />
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading form...</p>
        </div>
      </div>
    );
  }

  if (error && !formData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white border border-gray-200 p-8 max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Form Not Found</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white border border-gray-200 p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h1>
          <p className="text-gray-600 mb-6">
            Your form has been submitted successfully. We will review your responses shortly.
          </p>
          <div className="text-sm text-gray-500">
            You may close this window.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-white border border-gray-200 p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-100">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{formData?.template_name}</h1>
              {formData?.template_description && (
                <p className="text-gray-600 mt-1">{formData.template_description}</p>
              )}
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white border border-gray-200">
          {error && (
            <div className="p-4 bg-red-50 border-b border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="p-6 space-y-6">
            {formData?.fields.map((field) => (
              <div key={field.id}>
                {field.type !== 'checkbox' && (
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                )}
                {renderField(field)}
                {field.help_text && (
                  <p className="mt-1 text-sm text-gray-500">{field.help_text}</p>
                )}
                {validationErrors[field.id] && (
                  <p className="mt-1 text-sm text-red-500">{validationErrors[field.id]}</p>
                )}
              </div>
            ))}
          </div>

          {/* Submit Button */}
          <div className="p-6 bg-gray-50 border-t border-gray-200">
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 px-4 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Form'
              )}
            </button>
          </div>
        </form>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-gray-500">
          Powered by {APP_NAME}
        </div>
      </div>
    </div>
  );
};

export default PublicFormView;


